import { Link } from 'react-router';
import { AlertCircle } from 'lucide-react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';
import type { JobDetailDTO } from '@/types/db';

/**
 * `<JobErrorPanel>` — UI-SPEC § 3.6.3 failure-state panel.
 *
 * Renders a coral-bordered glass-strong panel with an AlertCircle icon, a
 * heading explaining the failure, body copy, and a "Resubmit this address"
 * link that pre-fills the SubmitForm via query string.
 *
 * Used in the JobDetail composite below the AgentProgressList when
 * `job.status === 'failed'`. The error_message column from a specific failed
 * agent is surfaced inside `<AgentProgressRow>` (coral message slot) so this
 * panel only carries the operator-level summary + resubmit action.
 *
 * Accessibility: `role="alert"` so the panel is announced when it appears.
 */
export interface JobErrorPanelProps {
  job: Pick<JobDetailDTO, 'address'>;
  className?: string;
}

export function JobErrorPanel({ job, className }: JobErrorPanelProps) {
  const resubmitHref = `/submit?address=${encodeURIComponent(job.address)}`;
  return (
    <GlassPanel
      variant="strong"
      as="aside"
      role="alert"
      className={cn(
        'p-6 border-coral-500/40 flex items-start gap-4 mt-6',
        className,
      )}
    >
      <AlertCircle
        aria-hidden="true"
        className="w-6 h-6 text-coral-500 shrink-0 mt-0.5"
      />
      <div className="flex flex-col gap-2 min-w-0">
        <h2 className="text-base font-semibold text-foam-50">
          This job failed to complete.
        </h2>
        <p className="text-sm text-foam-50/80">
          The Claude CLI process may have exited unexpectedly. You can
          resubmit the address to retry. Per-agent error messages are shown
          inline on the rows above.
        </p>
        <Link
          to={resubmitHref}
          className={cn(
            'text-tide-400 underline underline-offset-2 hover:text-tide-400/80',
            'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded',
            'text-sm font-semibold mt-1 self-start',
          )}
        >
          Resubmit this address →
        </Link>
      </div>
    </GlassPanel>
  );
}
